type BrowserPopStateGuard = (event: PopStateEvent) => boolean;

export type StateHistoryTraversalResult = 'expected' | 'unexpected' | 'unavailable';

export interface StateHistoryRouterPosition {
  readonly navigationId: number;
  readonly pageId: number;
}

export interface PendingNativeTraversal {
  readonly isExpectedState: () => boolean;
  readonly complete: (result: StateHistoryTraversalResult) => void;
}

let activeGuard: BrowserPopStateGuard | null = null;
let installed: boolean = false;
let pendingNativeTraversal: PendingNativeTraversal | null = null;

const ANGULAR_NAVIGATION_ID_STATE_KEY: string = 'navigationId';
const ANGULAR_ROUTER_PAGE_ID_STATE_KEY: string = 'ɵrouterPageId';
const HISTORY_TRAVERSAL_TIMEOUT_MS: number = 2_000;

const handlePopState = (event: PopStateEvent): void => {
  const traversal: PendingNativeTraversal | null = pendingNativeTraversal;
  if (traversal) {
    const isExpectedState: boolean = traversal.isExpectedState();
    if (isExpectedState) {
      event.stopImmediatePropagation();
      // Keep callers blocked until the current popstate dispatch has finished.
      setTimeout((): void => traversal.complete('expected'), 0);
      return;
    }
    traversal.complete('unexpected');
  }

  if (activeGuard?.(event)) {
    event.stopImmediatePropagation();
  }
};

export function installStateHistoryGuard(): void {
  if (installed || typeof window === 'undefined') return;

  installed = true;
  window.addEventListener('popstate', handlePopState, true);
}

export function setStateHistoryGuard(guard: BrowserPopStateGuard | null): void {
  activeGuard = guard;
}

export function readStateHistoryPageId(state: unknown): number | null {
  if (!isRecord(state)) return null;

  const pageId: unknown = state[ANGULAR_ROUTER_PAGE_ID_STATE_KEY];
  return typeof pageId === 'number' && Number.isSafeInteger(pageId) ? pageId : null;
}

export function readStateHistoryRouterPosition(state: unknown): StateHistoryRouterPosition | null {
  if (!isRecord(state)) return null;

  const navigationId: unknown = state[ANGULAR_NAVIGATION_ID_STATE_KEY];
  const pageId: number | null = readStateHistoryPageId(state);
  return typeof navigationId === 'number'
    && Number.isSafeInteger(navigationId)
    && navigationId > 0
    && pageId !== null
    ? {navigationId, pageId}
    : null;
}

export function createStateHistoryRouterState(
  position: StateHistoryRouterPosition,
): Record<string, unknown> {
  return {
    [ANGULAR_NAVIGATION_ID_STATE_KEY]: position.navigationId,
    [ANGULAR_ROUTER_PAGE_ID_STATE_KEY]: position.pageId,
  };
}

export function traverseStateHistory(
  delta: number,
  isExpectedState: () => boolean,
): Promise<StateHistoryTraversalResult> {
  if (typeof history === 'undefined' || pendingNativeTraversal) return Promise.resolve('unavailable');

  return new Promise<StateHistoryTraversalResult>((resolve): void => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let completed: boolean = false;
    const complete = (result: StateHistoryTraversalResult): void => {
      if (completed) return;

      completed = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (pendingNativeTraversal?.complete === complete) {
        pendingNativeTraversal = null;
      }
      resolve(result);
    };
    pendingNativeTraversal = {complete, isExpectedState};
    timeoutId = setTimeout(
      (): void => complete('unavailable'),
      HISTORY_TRAVERSAL_TIMEOUT_MS,
    );
    try {
      history.go(delta);
    } catch {
      complete('unavailable');
    }
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
